import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FormSections, H3, Spinner, useLocalizeMessage } from 'libs.nucleus';
import { AclScope, ListInformation, PageHeader, useBooleanState } from 'libs.react';
import { ApiClientService, AuthContext, useApiClient, useScopeValidator } from 'libs.react_internal';
import { DataTransferTemplateContext } from '../../../contexts/data_transfer_template/data_transfer_template.context';
import { ChangeTransferStatusModal } from '../../components/data_transfer_template/change_transfer_status.component';
import { EditTransferTemplateModal } from '../../components/data_transfer_template/edit_transfer_template_modal.component';
const DataTransferTemplateViewPage = () => {
    const translate = useLocalizeMessage();
    const navigate = useNavigate();
    const { entityId, user } = useContext(AuthContext);
    const webClient = useApiClient(ApiClientService.WEB);
    const { transferTemplateId } = useParams();
    const { packageTypeOptions, fileExtensionOptions, getStatusBadge } = useContext(DataTransferTemplateContext);
    const [isEditTransferTemplateModalOpen, openEditTransferTemplateModal, closeEditTransferTemplateModal] = useBooleanState();
    const [isChangeTransferStatusModalOpen, openChangeTransferStatusModal, closeChangeTransferStatusModal] = useBooleanState();
    // Whether the shared edit wizard should open in locked, read-only ("View") mode.
    const [isTemplateModalReadOnly, setIsTemplateModalReadOnly] = useState(false);
    const retrieveTransferTemplateDetails = async () => {
        const { data } = await webClient.post(`/api/data_transfer/templates/${transferTemplateId}`);
        return data;
    };
    const { data, isLoading } = useQuery({
        queryKey: ['entities', entityId, 'transfer_templates', transferTemplateId],
        queryFn: retrieveTransferTemplateDetails,
        enabled: !!user && !!entityId,
    });
    // Fine-grained edit permission for this specific template. Mirrors the backend's
    // `dt.transferTemplate.update` ACL check, so users without it get a read-only ("View") experience.
    const canEditTemplate = useScopeValidator(AclScope.TRANSFER_TEMPLATE_UPDATE, { resource: data?.path });
    const pageTitle = useMemo(() => {
        if (!data) {
            return '';
        }
        return (_jsxs("div", { className: 'flex gap-4 items-center', children: [_jsx(H3, { className: 'text-4xl font-light text-text-primary', children: data.name }), _jsx("div", { className: 'pt-1', children: getStatusBadge(data.status) })] }));
    }, [data, getStatusBadge]);
    const openEditTemplate = () => {
        setIsTemplateModalReadOnly(false);
        openEditTransferTemplateModal();
    };
    const openViewTemplate = () => {
        setIsTemplateModalReadOnly(true);
        openEditTransferTemplateModal();
    };
    const headerOptions = useMemo(() => {
        const options = [];
        if (!data) {
            return options;
        }
        // Changing status (publish/archive) is a mutation, so it requires edit permission and only applies
        // to non-archived templates.
        if (canEditTemplate && (data.status === 'PUBLISHED' || data.status === 'DRAFT')) {
            options.push({ name: translate('Change status'), onClick: openChangeTransferStatusModal, variant: 'secondary' });
        }
        // Templates are only editable while in DRAFT and when the user holds edit permission; the backend
        // rejects content edits on PUBLISHED/ARCHIVED templates. Everyone else opens the same wizard in a
        // locked, read-only ("View") mode so they can inspect the full configuration.
        if (canEditTemplate && data.status === 'DRAFT') {
            options.push({ name: translate('Edit'), onClick: openEditTemplate, variant: 'primary' });
        }
        else {
            options.push({ name: translate('View'), onClick: openViewTemplate, variant: 'primary' });
        }
        return options;
    }, [data, canEditTemplate, translate, openChangeTransferStatusModal, openEditTransferTemplateModal]);
    if (isLoading) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    const originLink = '/library/data-templates';
    if (!data) {
        navigate(originLink);
        return;
    }
    const breadcrumbsPages = [
        { name: translate('Data templates'), path: originLink },
        { name: data.name, path: '' },
    ];
    const formSections = [
        {
            title: translate('Details'),
            content: (_jsx(ListInformation, { items: [
                    { title: translate('Name'), description: data.name },
                    { title: translate('Description'), description: data.description },
                    {
                        title: translate('Package type'),
                        description: packageTypeOptions.find((option) => option.value === data.packageType)?.label ?? '',
                    },
                    {
                        title: translate('File type'),
                        description: fileExtensionOptions.find((option) => option.value === data.fileExtension)?.label ?? '',
                    },
                ] })),
        },
    ];
    return (_jsxs("section", { className: 'relative p-6 h-full flex flex-col divide-y', children: [_jsx(PageHeader, { breadcrumbPages: breadcrumbsPages, title: pageTitle, options: headerOptions, LinkComponent: Link }), _jsx("div", { className: 'py-8', children: _jsx(FormSections, { sections: formSections }) }), isEditTransferTemplateModalOpen && (_jsx(EditTransferTemplateModal, { initialData: data, readOnly: isTemplateModalReadOnly, onClose: closeEditTransferTemplateModal })), isChangeTransferStatusModalOpen && (_jsx(ChangeTransferStatusModal, { templateId: data.id, currentStatus: data.status, onClose: closeChangeTransferStatusModal }))] }));
};
export default DataTransferTemplateViewPage;
